import * as React from "react";

// About two rows on desktop; "+N more" expands the rest.
const DEFAULT_TOP_N = 12;

interface CulturePickerProps {
  /** Full list of culture/country names to display as pills. */
  options: string[];
  selectedFilters: string[];
  /** The shared keyword query — used to rank and highlight pills. */
  keywordQuery: string;
  onToggle: (option: string) => void;
  onClearFilters: () => void;
  topN?: number;
}

/**
 * Score an option against an array of keyword tokens.
 * Returns a positive number for any match; higher = stronger match.
 */
function scoreOption(option: string, tokens: string[]): number {
  if (tokens.length === 0) return 0;
  const lower = option.toLowerCase();
  let score = 0;
  for (const token of tokens) {
    if (!token) continue;
    const t = token.toLowerCase();
    if (lower === t) {
      score += 100;
    } else if (lower.startsWith(t)) {
      score += 60;
    } else if (lower.includes(t)) {
      score += 30;
    } else if (t.startsWith(lower)) {
      // token is longer but starts with this option name — weaker signal
      score += 10;
    }
  }
  return score;
}

/**
 * Wrap matched substrings in <strong> for bold highlighting.
 */
function HighlightedLabel({
  label,
  tokens,
}: {
  label: string;
  tokens: string[];
}) {
  if (tokens.length === 0) return <>{label}</>;

  // Build a regex that matches any token (case-insensitive)
  const escaped = tokens
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  if (!escaped) return <>{label}</>;

  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = label.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? <strong key={i}>{part}</strong> : part,
      )}
    </>
  );
}

export const CulturePicker: React.FC<CulturePickerProps> = ({
  options,
  selectedFilters,
  keywordQuery,
  onToggle,
  onClearFilters,
  topN = DEFAULT_TOP_N,
}) => {
  const [showActive, setShowActive] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);

  const tokens = React.useMemo(
    () =>
      keywordQuery
        .split(/\s+/)
        .map((t) => t.trim())
        .filter(Boolean),
    [keywordQuery],
  );

  // Score + sort options: selected items always bubble up within their score tier
  const scored = React.useMemo(() => {
    return options
      .map((opt) => ({ opt, score: scoreOption(opt, tokens) }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.opt.localeCompare(b.opt);
      });
  }, [options, tokens]);

  // When in "show active" mode, only show selected filters — sorted by score
  // first so keyword-matching active filters bubble to the top
  const pillsToShow: string[] = React.useMemo(() => {
    if (showActive) {
      return selectedFilters.slice().sort((a, b) => {
        const scoreDiff = scoreOption(b, tokens) - scoreOption(a, tokens);
        if (scoreDiff !== 0) return scoreDiff;
        return a.localeCompare(b);
      });
    }
    const all = scored.map((s) => s.opt);
    return showAll ? all : all.slice(0, topN);
  }, [showActive, showAll, scored, selectedFilters, topN]);

  const activeCount = selectedFilters.length;
  const hasMore = !showActive && !showAll && scored.length > topN;

  return (
    <div className="culture-picker">
      {/* Summary / mode toggle */}
      <div className="culture-picker-header">
        <span className="culture-picker-summary culture-picker-summary--empty">
          cultures &amp; countries
        </span>
        {activeCount > 0 && (
          <button
            type="button"
            className="culture-picker-summary culture-picker-summary--active p-0 m-0"
            onClick={() => setShowActive((v) => !v)}
          >
            {showActive
              ? "← show all filters"
              : `${activeCount} active filter${activeCount !== 1 ? "s" : ""} →`}
          </button>
        )}
        {activeCount > 0 && !showActive && (
          <button
            type="button"
            className="culture-picker-clear p-0 m-0"
            onClick={onClearFilters}
          >
            clear all
          </button>
        )}
      </div>

      {/* Pill grid */}
      <div className="culture-picker-pills">
        {pillsToShow.map((opt) => {
          const isSelected = selectedFilters.includes(opt);
          const score = scoreOption(opt, tokens);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={[
                "culture-pill",
                isSelected
                  ? "culture-pill--selected"
                  : "culture-pill--unselected",
                score > 0 ? "culture-pill--match" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              title={isSelected ? `Remove ${opt}` : `Filter by ${opt}`}
            >
              <span className="culture-pill-prefix">
                {isSelected ? "×" : "+"}
              </span>
              <span className="culture-pill-label">
                <HighlightedLabel label={opt} tokens={score > 0 ? tokens : []} />
              </span>
            </button>
          );
        })}

        {hasMore && (
          <button
            type="button"
            className="culture-pill culture-pill--show-more"
            onClick={() => setShowAll(true)}
          >
            +{scored.length - topN} more
          </button>
        )}

        {showAll && !showActive && (
          <button
            type="button"
            className="culture-pill culture-pill--show-more"
            onClick={() => setShowAll(false)}
          >
            show less
          </button>
        )}
      </div>
    </div>
  );
};
